import { jwtDecode } from "jwt-decode";

/**
 * Explicit application roles (Oct-10 QA #4).
 *
 * The three roles are distinct. Role resolution uses ordered, token-based
 * precedence — NOT loose substring matching — so that PLATFORM_SUPER_ADMIN
 * (whose string also contains "admin") is never collapsed into DISTRIBUTOR.
 */
export type Role = "BUYER" | "DISTRIBUTOR" | "PLATFORM_SUPER_ADMIN";

function collectClaimStrings(payload: any): string[] {
  if (!payload || typeof payload !== "object") return [];
  const keys = ["role", "roles", "authorities", "scope", "scopes", "userType", "type", "authority"];
  const out: string[] = [];
  for (const k of keys) {
    const v = payload[k];
    if (!v) continue;
    if (Array.isArray(v)) v.forEach((x) => out.push(String(x)));
    else out.push(String(v));
  }
  return out.map((s) => s.toLowerCase());
}

/**
 * Map a role-ish blob to a Role with strict precedence:
 *   1) Platform super admin  (cross-tenant platform owner)
 *   2) Buyer                 (customer / shopper)
 *   3) Distributor           (distributor admin / seller / staff)
 *
 * Super admin is tested FIRST and by its own explicit markers, so a token
 * such as "PLATFORM_SUPER_ADMIN" / "ROLE_SUPER_ADMIN" can never fall through
 * to the distributor branch just because it contains the word "admin".
 */
function detectRole(blob: string): Role | null {
  const b = (blob || "").toLowerCase();
  if (!b.trim()) return null;

  // 1) Platform super admin — the platform owner across all tenants.
  if (
    b.includes("platform_super_admin") ||
    b.includes("super_admin") ||
    b.includes("superadmin") ||
    b.includes("platform") ||
    (b.includes("super") && b.includes("admin"))
  ) {
    return "PLATFORM_SUPER_ADMIN";
  }

  // 2) Buyer / customer.
  if (b.includes("buyer") || b.includes("customer") || b.includes("shopper")) {
    return "BUYER";
  }

  // 3) Distributor admin / seller / staff. "admin" here only ever means a
  //    distributor-level admin because super admin was already ruled out above.
  if (
    b.includes("distributor") ||
    b.includes("seller") ||
    b.includes("vendor") ||
    b.includes("staff") ||
    b.includes("manager") ||
    b.includes("admin")
  ) {
    return "DISTRIBUTOR";
  }

  return null;
}

/** Normalize any role-ish value coming from the backend. Returns null when it can't tell. */
export function normalizeRole(raw: unknown): Role | null {
  if (raw == null) return null;
  const vals = Array.isArray(raw) ? raw : [raw];
  return detectRole(vals.map((v) => String(v)).join(" ").toLowerCase());
}

/** Role from a JWT's claims, or null when the token carries no usable role claim. */
export function roleFromTokenOrNull(token: string | null): Role | null {
  if (!token) return null;
  try {
    const payload: any = jwtDecode(token);
    return detectRole(collectClaimStrings(payload).join(" "));
  } catch {
    return null; // opaque / non-JWT token
  }
}

/** Best-effort role extraction from a JWT. Falls back to BUYER. */
export function roleFromToken(token: string | null): Role {
  return roleFromTokenOrNull(token) ?? "BUYER";
}

export function usernameFromToken(token: string | null): string {
  if (!token) return "";
  try {
    const payload: any = jwtDecode(token);
    return payload.sub || payload.username || payload.name || payload.email || "";
  } catch {
    return "";
  }
}

export const homePathForRole = (role: Role): string => {
  switch (role) {
    case "PLATFORM_SUPER_ADMIN": return "/admin";
    case "DISTRIBUTOR":          return "/dashboard";
    default:                     return "/catalogue";
  }
};

/** Numeric user id from a JWT's claims (userid/userId/id/uid), or null. */
export function useridFromToken(token: string | null): number | null {
  if (!token) return null;
  try {
    const payload: any = jwtDecode(token);
    const v = payload.userid ?? payload.userId ?? payload.id ?? payload.uid;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

/** Human-friendly name for display/customer label (prefers the name claim). */
export function displayNameFromToken(token: string | null): string {
  if (!token) return "";
  try {
    const payload: any = jwtDecode(token);
    return payload.name || payload.username || payload.sub || payload.email || "";
  } catch {
    return "";
  }
}

/** Account status claim from a JWT (PENDING | APPROVED | SUSPENDED), or null. */
export function statusFromToken(token: string | null): string | null {
  if (!token) return null;
  try {
    const payload: any = jwtDecode(token);
    return payload.status ?? null;
  } catch {
    return null;
  }
}

/** Numeric tenant id claim from a JWT (tenantId/tenant/tid), or null (super admin). */
export function tenantFromToken(token: string | null): number | null {
  if (!token) return null;
  try {
    const payload: any = jwtDecode(token);
    const v = payload.tenantId ?? payload.tenant ?? payload.tid;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

/** True when the JWT is an active impersonation ("view-as-tenant") session (Phase D). */
export function impersonatingFromToken(token: string | null): boolean {
  if (!token) return false;
  try {
    const payload: any = jwtDecode(token);
    return payload.imp === true || payload.imp === "true" || !!payload.impersonating;
  } catch {
    return false;
  }
}
